import variables from './_variables'
import type {FinalData} from './load_data'
import {getData} from './load_data'
import {Feature} from 'ol'
import type {Extent} from 'ol/extent'
import * as olExtent from 'ol/extent'
import type {FeatureLike} from 'ol/Feature'
import {Polygon} from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import {Fill, Stroke, Style, Text} from 'ol/style'

const createTextStyle = function (text: string, resolution: number) {
  const size = 10 / resolution + 'px'
  const height = '1.1'
  const weight = '600'
  const font = weight + ' ' + size + '/' + height + ' sans-serif'

  return new Text({
    font,
    text,
    fill: new Fill({color: '#fff'}),
  })
}

// Polygons
export const polygonStyleFunction = (feature: FeatureLike, resolution: number, hovered: boolean | undefined = undefined) => {
  let statusColor: string
  const status: string | undefined = feature.get('status')
  if (status && Object.hasOwn(variables, status)) {
    statusColor = hovered ? variables[status].hover : variables[status].base
  } else {
    statusColor = variables['unset'].base
  }

  return new Style({
    stroke: new Stroke({
      color: '#aeaeae',
      width: 1.2,
      lineCap: 'round',
      lineJoin: 'round',
    }),
    fill: new Fill({
      color: statusColor,
    }),
    text: createTextStyle(feature.get('name'), resolution),
  })
}

// Polygons
export const specialStyleFunction = (_feature: FeatureLike, _resolution: number, hovered: boolean | undefined = undefined) => {
  return new Style({
    stroke: new Stroke({
      color: hovered ? '#67b9ef' : '#3498db',
      width: 3,
      lineCap: 'round',
      lineJoin: 'round',
    }),
    fill: new Fill({
      color: 'transparent',
    }),
  })
}

const adjustCoords = (coords: number[][], mapHeight: number) => {
  return coords.map((coordsPair) => {
    return [coordsPair[0], mapHeight - coordsPair[1]]
  })
}

export const generatePolygons: ($: JQueryStatic, mapHeight: number) => Promise<{
  data: FinalData;
  featuresExtent: Extent;
  vectorLayer: VectorLayer<VectorSource>;
  specialLayer: VectorLayer<VectorSource>;
}> = async ($, mapHeight) => {
  const data = await getData($)
  const features: Feature[] = []
  const featuresSpecial: Feature[] = []
  for (const dataKey in data.plots) {
    if (Object.hasOwn(data.plots, dataKey)) {
      const coords = data.plots[dataKey].coords
      if (coords && coords.length > 0) {
        const adjustedCoords = adjustCoords(coords, mapHeight)
        const newFeature = new Feature({
          geometry: new Polygon([adjustedCoords]),
          type: 'Plot',
        })
        newFeature.setProperties({
          name: dataKey,
          status: data.plots[dataKey].field_status_export,
        })
        features.push(newFeature)
      }
    }
  }

  // Special zones
  for (const specialZone of data.special) {
    if (specialZone.coords && specialZone.coords.length > 0) {
      const adjustedCoords = adjustCoords(specialZone.coords, mapHeight)
      const newFeature = new Feature({
        geometry: new Polygon([adjustedCoords]),
        type: 'Special',
      })
      featuresSpecial.push(newFeature)
    }
  }

  const combinedFeatures = features.concat(featuresSpecial)
  let featuresExtent = olExtent.createEmpty()
  for (const feat of combinedFeatures) {
    const geom = feat.getGeometry()
    if (geom) {
      featuresExtent = olExtent.extend(featuresExtent, geom.getExtent())
    }
  }

  return {
    data,
    featuresExtent,
    vectorLayer: new VectorLayer({
      source: new VectorSource({
        features,
      }),
      style(feature, resolution) {
        return polygonStyleFunction(feature, resolution)
      },
    }),
    specialLayer: new VectorLayer({
      source: new VectorSource({
        features: featuresSpecial,
      }),
      style(feature, resolution) {
        return specialStyleFunction(feature, resolution)
      },
    }),
  }
}
